import { Add } from "@carbon/icons-react";
import { Button, Layer, TableToolbar, TableToolbarContent, Tooltip } from "@carbon/react";
import { SingleSelectAppliedFilter, useModal } from "@flaminem/ui-components";
import { FC, useEffect, useMemo, useState } from "react";

import { AuthorizedItem } from "src/authentication/AuthorizedMenu.tsx";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import { AppliedFilters } from "src/components/Filters/AppliedFilters.tsx";
import { ModalTransition } from "src/components/Modal";
import { TableToolbarSearch } from "src/components/Tables/TableToolbarSearch";
import { debounceSearchTime } from "src/hooks/useSearch.ts";
import { useT } from "src/internationalization";
import { debounce } from "src/utils/functions.utils.ts";

import { useEditableProperties } from "../useEditableProperties";

import { BlacklistElementModal } from "./BlacklistElementModal";
import { BlacklistsFilter } from "./BlacklistsFilter";

export type BlacklistsTableToolbarProps = {
  onElementCreated: () => void;
  searchTerm: string;
  handleOnSearchTermChange: (searchTerm: string) => void;
  filterValues: { includeDisabledElements: boolean };
  handleOnFiltersChange: (filters: { includeDisabledElements: boolean }) => void;
};

export const BlacklistsTableToolbar: FC<BlacklistsTableToolbarProps> = ({
  onElementCreated,
  searchTerm,
  handleOnSearchTermChange,
  handleOnFiltersChange,
  filterValues,
}) => {
  const t = useT();
  const [currentSearchTerm, setCurrentSearchTerm] = useState(searchTerm);
  const createModal = useModal();

  const { editProperties, disable: disableCreate } = useEditableProperties();

  useEffect(() => {
    setCurrentSearchTerm(searchTerm);
  }, [searchTerm]);

  const debouncedSearch = useMemo(() => {
    return debounce(handleOnSearchTermChange, debounceSearchTime);
  }, [handleOnSearchTermChange]);

  const setSearchTerm = (value: string) => {
    setCurrentSearchTerm(value);
    debouncedSearch(value);
  };

  const button = (
    <Button
      renderIcon={Add}
      kind="secondary"
      onClick={createModal.openModal}
      disabled={disableCreate}
    >
      {t("page.blacklists.table.button.create")}
    </Button>
  );

  return (
    <>
      <TableToolbar aria-label="data table toolbar">
        <TableToolbarContent className="border-b border-solid border-borderSubtle01">
          <TableToolbarSearch
            placeholder={t("page.blacklists.table.search.placeholder")}
            onSearchChange={setSearchTerm}
            search={currentSearchTerm}
          />
          <BlacklistsFilter
            onApplyFilter={handleOnFiltersChange}
            onResetFilter={() => handleOnFiltersChange({ includeDisabledElements: false })}
            values={filterValues}
          />
          <AuthorizedItem
            requiredPermissions={[
              { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.CREATE] },
            ]}
          >
            {disableCreate ? (
              <Tooltip label={t("page.blacklists.table.button.disable")} align="bottom">
                <span className="inline-block">{button}</span>
              </Tooltip>
            ) : (
              button
            )}
          </AuthorizedItem>
        </TableToolbarContent>
      </TableToolbar>
      <ModalTransition>
        {createModal.isModalOpen && (
          <BlacklistElementModal
            onClose={createModal.closeModal}
            onSuccess={() => {
              onElementCreated();
              createModal.closeModal();
            }}
            editProperties={editProperties}
          />
        )}
      </ModalTransition>
      {filterValues.includeDisabledElements && (
        <div className="flex w-full bg-layer">
          <Layer className={"p-5"}>
            <AppliedFilters
              onClear={() => handleOnFiltersChange({ includeDisabledElements: false })}
            >
              <SingleSelectAppliedFilter
                item={{
                  label: t("page.blacklists.table.filters.applied.disabled.filter.value"),
                  value: "true",
                }}
                title={t("page.blacklists.table.filters.applied.disabled.filter.title")}
                onClear={() => handleOnFiltersChange({ includeDisabledElements: false })}
              />
            </AppliedFilters>
          </Layer>
        </div>
      )}
    </>
  );
};
